import React from 'react';
import Icon from 'components/commons/icon/Icon';
import Loading from 'components/commons/loading/Loading';

import * as api from 'config/api';
import * as consts from 'config/consts';
import { formatDateForString } from 'utils/formatter';
import { useRouting } from 'hooks/useRouting';
import { Link } from 'react-router-dom';
import Modal from './Modal';

type BrokerListProps = {
  brokers: Array<api.GetUserBrokerResultItem> | undefined;
  isAuthenticatedMoneytree?: boolean | null;
  isLinkedMoneytree?: boolean | null;
};

type UpdateDateTimeModalProps = {
  brokers: Array<api.GetUserBrokerResultItem>;
  onClose: () => void;
  beInRefresh: boolean;
};

export const BrokerList: React.FC<BrokerListProps> = ({
  brokers,
  isAuthenticatedMoneytree,
  isLinkedMoneytree,
}) => {
  // 手入力口座を省き、更新日時が最新のBrokerID毎の連携口座一覧データ
  const NonManualAssetBrokerList = Object.values(
    (brokers || []).reduce(
      (acc, current) => {
        const brokerID = current.BrokerID;
        if (!acc[brokerID]) {
          acc[brokerID] = current;
          return acc;
        }
        acc[brokerID] =
          new Date(acc[brokerID].UpdTime).getTime() <
          new Date(current.UpdTime).getTime()
            ? current
            : acc[brokerID];
        return acc;
      },
      {} as {
        [key: string]: api.GetUserBrokerResultItem;
      }
    )
  ).filter(x => x.AggregateSource !== consts.AggregateSourceTypes.手入力);

  // 更新日時が最新のマネーツリー口座
  const moneyTreeBroker = NonManualAssetBrokerList.filter(
    x => x.AggregateSource === consts.AggregateSourceTypes.マネーツリー
  ).reduce((prev, current) => {
    if (!prev[0]) return [current];
    return new Date(prev[0].UpdTime).getTime() >
      new Date(current.UpdTime).getTime()
      ? prev
      : [current];
  }, [] as api.GetUserBrokerResultItem[]);

  // 連携済のテコテック、MONO全口座に更新日時が最新のマネーツリー口座を追加
  const BrokerList = NonManualAssetBrokerList.filter(
    x =>
      x.AggregateSource === consts.AggregateSourceTypes.テコテック ||
      x.AggregateSource === consts.AggregateSourceTypes.MONO
  )
    .concat(moneyTreeBroker)
    .sort((a, b) => b.AggregateSource - a.AggregateSource);

  const isIncludingMoneyTreeBroker =
    BrokerList.filter(
      x => x.AggregateSource === consts.AggregateSourceTypes.マネーツリー
    ).length > 0;

  return (
    <>
      {BrokerList?.map(
        ({ BrokerName, UpdTime, Status, AggregateSource }, index) => {
          return (
            <React.Fragment key={index}>
              <div className="flex text-sm w-full">
                {AggregateSource === consts.AggregateSourceTypes.テコテック ||
                AggregateSource === consts.AggregateSourceTypes.MONO ? (
                  <>
                    <div className="w-1/2 truncate">{BrokerName}</div>
                    <div className="w-1/2 flex justify-end">
                      {Status === '000' ? (
                        <>
                          {formatDateForString(
                            UpdTime,
                            'yyyy/MM/dd(ddd) HH:mm'
                          )}
                        </>
                      ) : (
                        <div className="form-error-note flex justify-end">
                          <div className="icon-alert"></div>
                          <div className="info-label">更新失敗</div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-1/2">Moneytree連携</div>
                    <div className="w-1/2 flex justify-end">
                      {isAuthenticatedMoneytree === false ? (
                        <Link to="securityaccountslink">
                          <div className="form-error-note">
                            <div className="icon-alert"></div>
                            <div className="info-label">
                              認証が切れました。再度連携を行ってください。
                            </div>
                          </div>
                        </Link>
                      ) : Status === '000' ? (
                        <>
                          {formatDateForString(
                            UpdTime,
                            'yyyy/MM/dd(ddd) HH:mm'
                          )}
                        </>
                      ) : (
                        <div className="form-error-note flex justify-end">
                          <div className="icon-alert"></div>
                          <div className="info-label">更新失敗</div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </React.Fragment>
          );
        }
      )}
      {!isIncludingMoneyTreeBroker &&
        !isAuthenticatedMoneytree &&
        isLinkedMoneytree && (
          <>
            <div className="flex text-sm">
              <div className="w-1/2">Moneytree連携</div>
              <div className="w-1/2 flex justify-end">
                <Link to="securityaccountslink">
                  <div className="form-error-note">
                    <div className="icon-alert"></div>
                    <div className="info-label">
                      認証が切れました。再度連携を行ってください。
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </>
        )}
    </>
  );
};

export const UpdateDateTimeModalInternal: React.FC<UpdateDateTimeModalProps & {
  title: string;
}> = props => {
  const { brokers, onClose, beInRefresh, title } = props;
  const routing = useRouting();
  function handleClickSecurityaccounts(): void {
    routing.pushRouting({ path: '/securityaccounts' }, true);
  }

  return (
    <Modal onClose={onClose}>
      <div className="flex mt-2 mx-3 p-2">
        <div className="flex-grow text-left text-lg font-bold">{title}</div>
        <button onClick={onClose}>
          <Icon name="close" className="text-2xl text-gray-500 text-center" />
        </button>
      </div>
      {beInRefresh ? (
        <Loading loading={beInRefresh} circularLoading={true} style={{}} />
      ) : (
        <div className="mx-3">
          <BrokerList brokers={brokers} />
        </div>
      )}
      <div className="ui-border-b mx-3 my-5"></div>
      <div className="mx-3 mb-3">
        証券口座のデータは平日の18:00以降にデータの取得を開始します。証券会社や取引データの量により反映までお時間がかかることがあります。データの反映までしばらくお待ちください。
        <br />
        <br />
        データの更新が出来なかった場合は、翌日に再度更新処理をおこないます。更新の失敗が続く場合は、ご登録いただいている証券口座情報の確認を行なってください。
      </div>
      <div
        className="flex justify-center text-gray-500 m-1 p-2 cursor-pointer"
        onClick={handleClickSecurityaccounts}>
        連携口座一覧を確認
      </div>
      <div
        className="flex justify-center text-gray-500 m-1 p-2 cursor-pointer"
        onClick={onClose}>
        閉じる
      </div>
    </Modal>
  );
};

const UpdateDateTimeModal: React.FC<UpdateDateTimeModalProps> = props => {
  const { beInRefresh } = props;
  const title = beInRefresh ? '更新中' : '更新しました';
  return <UpdateDateTimeModalInternal {...{ ...props, title }} />;
};

export default UpdateDateTimeModal;
